import { createContext, useContext, useId } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Field.css";

/**
 * The wiring a labelled control needs. `Field` builds it once and hands it to
 * its child two ways: through context (so `Input` and `Textarea` pick it up
 * with no wiring at all) and through a render prop (for any other control).
 */
export interface FieldControlProps {
  id: string;
  required?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
}

const FieldContext = createContext<FieldControlProps | null>(null);

/** Returns the enclosing `Field`'s control wiring, or `null` when unwrapped. */
export function useFieldControl(): FieldControlProps | null {
  return useContext(FieldContext);
}

export interface FieldProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children" | "title"
> {
  label: string;
  /** Guidance shown under the control. Announced via `aria-describedby`. */
  hint?: string;
  /** Presence of this string puts the field in its invalid state. */
  error?: string;
  required?: boolean;
  /** Keeps the label in the accessibility tree but off the screen. */
  labelHidden?: boolean;
  /** Overrides the generated id when the control already owns one. */
  htmlFor?: string;
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}

export function Field({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  htmlFor,
  className,
  children,
  ...rest
}: FieldProps) {
  const uid = useId();
  const id = htmlFor ?? `${uid}control`;
  const hintId = hint !== undefined ? `${uid}hint` : undefined;
  const errorId = error !== undefined ? `${uid}error` : undefined;

  const describedBy =
    [hintId, errorId]
      .filter((value): value is string => value !== undefined)
      .join(" ") || undefined;

  const control: FieldControlProps = {
    id,
    required: required || undefined,
    "aria-describedby": describedBy,
    "aria-invalid": error !== undefined ? true : undefined,
  };

  const classes = [
    "fc-field",
    error !== undefined ? "is-invalid" : null,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <FieldContext.Provider value={control}>
      <div {...rest} className={classes}>
        <label
          className={
            labelHidden
              ? "fc-field__label fc-field__label--hidden"
              : "fc-field__label"
          }
          htmlFor={id}
        >
          {label}
          {required ? (
            <span className="fc-field__required" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        <div className="fc-field__control">
          {typeof children === "function" ? children(control) : children}
        </div>

        {hint !== undefined ? (
          <p className="fc-field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}

        {error !== undefined ? (
          <p className="fc-field__error" id={errorId}>
            <svg
              className="fc-field__error-icon"
              viewBox="0 0 16 16"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            >
              <circle
                cx="8"
                cy="8"
                r="6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M8 4.75v4M8 11.1v.15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
